import React from 'react';
import { Game } from './GameSelector';
import { Audience } from './AudienceSelector';
import RemoteFile from '@/helper/RemoteFile';
import { LocationItem } from './LocationSelector';

interface Props {
  name: string;
  game?: Game;
  audience?: Audience;
  customGender: string[];
  customAge: [number, number];
  customLocation: LocationItem[];
  start?: string;
  end?: string;
  daily: number;
  totalDays: number;
}

type LocationItem2 = {
  target_level: 'place' | 'region' | 'country';
  name: string;
  region_name?: string;
  country_name?: string;
};

export const CampaignPreview: React.FC<Props> = ({
  name,
  game,
  audience,
  customGender,
  customAge,
  customLocation,
  start,
  end,
  daily,
  totalDays,
}) => {
  function formatLocalLocation(loc: LocationItem): string {
    const city = loc.city?.name;
    const subregion = loc.subregion?.name;
    const country = loc.country?.name;

    return [city, subregion, country].filter(Boolean).join(', ');
  }

  function formatLocation(loc: LocationItem2): string {
    const city = loc.target_level === 'place' ? loc.name : null;
    const subregion =
      loc.target_level === 'region' || loc.target_level === 'place'
        ? loc.region_name
        : null;
    const country =
      loc.target_level === 'country' ||
      loc.target_level === 'region' ||
      loc.target_level === 'place'
        ? loc.country_name
        : null;

    return [city, subregion, country].filter(Boolean).join(', ');
  }

  function normalizeToArray(val: string | string[] | undefined): string[] {
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') return [val];
    return [];
  }

  const genderList = [
    ...normalizeToArray(audience?.gender),
    ...(audience ? [] : customGender),
  ];

  return (
    <div className='space-y-2 text-sm'>
      <h2 className='text-xl font-semibold'>Campaign Preview</h2>
      <div className='w-full flex justify-center py-4'>
        {game && (
          <RemoteFile
            className='w-[300] h-[500px] self-center'
            keyPath={game.file_url}
            key={game.title}
          />
        )}
      </div>

      <div className='bg-[#F5F5F5] p-4 rounded-2xl space-y-3'>
        {/* Campaign Name */}
        <div className='pb-3 border-b border-gray-300'>
          <label className='block text-sm font-normal text-gray-500 mb-1'>
            Campaign Name
          </label>
          <p className='text-gray-900'>{name}</p>
        </div>

        {/* Game Name */}
        <div className='pb-3 border-b border-gray-300'>
          <label className='block text-sm font-normal text-gray-500 mb-1'>
            Gamic Name
          </label>
          <p className='text-gray-900'>{game?.title}</p>
        </div>

        {/* Gender Target */}
        <div className='pb-3 border-b border-gray-300'>
          <label className='block text-sm font-normal text-gray-500 mb-1'>
            Gender Target
          </label>
          <div className='flex flex-wrap gap-2'>
            {genderList.map((g) => (
              <span
                key={g}
                className='px-3 py-1 bg-white rounded-full text-sm text-gray-800 shadow-sm'
              >
                {g}
              </span>
            ))}
          </div>
        </div>

        {/* Age Target */}
        <div className='pb-3 border-b border-gray-300'>
          <label className='block text-sm font-normal text-gray-500 mb-1'>
            Age Target
          </label>
          <p className='text-gray-900'>
            {audience
              ? `${audience.min_age}y - ${audience.max_age}y`
              : `${customAge[0]}y - ${customAge[1]}y`}
          </p>
        </div>

        {/* Location */}
        <div className='pb-3 border-b border-gray-300'>
          <label className='block text-sm font-normal text-gray-500 mb-1'>
            Location
          </label>
          <div className='flex flex-wrap gap-2'>
            {audience
              ? audience?.audience_locations?.map((loc: any, index: number) => {
                  return (
                    <span
                      key={index}
                      className='px-3 py-1 bg-white rounded-full text-sm text-gray-800 shadow-sm'
                    >
                      {formatLocation(loc)}
                    </span>
                  );
                })
              : customLocation?.map((loc, idx) => (
                  <span
                    key={idx}
                    className='px-3 py-1 bg-white rounded-full text-sm text-gray-800 shadow-sm'
                  >
                    {formatLocalLocation(loc)}
                  </span>
                ))}
          </div>
        </div>

        {/* Date Range */}
        <div className='pb-3 border-b border-gray-300'>
          <label className='block text-sm font-normal text-gray-500 mb-1'>
            Date Range
          </label>
          {start && end && (
            <p className='text-gray-900'>
              {new Date(start).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}{' '}
              -{' '}
              {new Date(end).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          )}
        </div>

        {/* Daily Budget */}
        <div>
          <label className='block text-sm font-normal text-gray-500 mb-1'>
            Daily Budget
          </label>
          <p className='text-gray-900'>${daily}</p>
        </div>
      </div>
    </div>
  );
};
